import React from 'react';
import PropTypes from 'prop-types';
import ReactDatePicker from 'react-datepicker';
import format from 'date-fns/format';
import { ReactComponent as CalendarIcon } from 'products/nomad_insurance/assets/img/icons/calendar.svg';
import { ReactComponent as ChevronLeft } from 'assets/img/icons/chevron-left.svg';
import 'react-datepicker/dist/react-datepicker.css';
import MONTHS from 'assets/data/months';
import {
  DatePickerContainer,
  StyledSelectedDate,
  Label,
  MonthHeader,
  ChevronRight
} from './styled';

const SelectedDate = React.forwardRef(
  ({ value, onClick, disabled, label, variant, color }, ref) => (
    <StyledSelectedDate
      onClick={onClick}
      ref={ref}
      isPlaceholder={!value}
      variant={variant}
      color={color}
    >
      {label && <Label>{label}</Label>}
      {value ? format(new Date(value), 'MMMM dd, yyyy') : 'Select a date'}
      {!disabled && <CalendarIcon />}
    </StyledSelectedDate>
  )
);

SelectedDate.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string
};

const DatePicker = ({
  label,
  selectedDate,
  maxDate,
  minDate,
  startDate,
  endDate,
  onDateSelected,
  selectsStart,
  selectsEnd,
  disabled = false,
  className,
  filterDate,
  variant,
  color
}) => {
  return (
    <DatePickerContainer className={className}>
      <ReactDatePicker
        formatWeekDay={(nameOfDay) => nameOfDay.charAt(0)}
        showPopperArrow={false}
        customInput={
          <SelectedDate
            disabled={disabled}
            label={label}
            variant={variant}
            color={color}
          />
        }
        disabled={disabled}
        renderCustomHeader={({
          date,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled
        }) => (
          <MonthHeader>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <ChevronLeft />
            </button>

            <select
              value={MONTHS[date.getMonth()]}
              onChange={({ target: { value } }) => changeMonth(MONTHS.indexOf(value))}
            >
              {MONTHS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <ChevronRight />
            </button>
          </MonthHeader>
        )}
        selected={selectedDate}
        placeholderText="Select date"
        maxDate={maxDate}
        minDate={minDate}
        startDate={startDate}
        endDate={endDate}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        onChange={(date) => onDateSelected(date)}
        filterDate={filterDate}
      />
    </DatePickerContainer>
  );
};

DatePicker.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onDateSelected: PropTypes.func.isRequired,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  selectsStart: PropTypes.bool,
  selectsEnd: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  filterDate: PropTypes.func,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'large']),
  color: PropTypes.oneOf(['white', 'transparentWhite'])
};

DatePicker.defaultProps = {
  filterDate: () => true
};

export default DatePicker;
